// components/AIChatbot/ChatInput.js

import React, { FormEvent, KeyboardEvent, useState } from "react";
import { Box, TextField, IconButton, makeStyles } from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import { tokens } from "../../theme/tokens";

const useStyles = makeStyles(() => ({
  inputContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
  },
  textField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
      backgroundColor: tokens.color.surface.sunken,
      "& fieldset": {
        borderColor: tokens.color.border.default,
      },
      "&:hover fieldset": {
        borderColor: tokens.color.border.strong,
      },
      "&.Mui-focused fieldset": {
        borderColor: tokens.color.focus.ring,
      },
    },
  },
  sendButton: {
    backgroundColor: tokens.color.brand.primaryAction,
    color: tokens.color.brand.onPrimary,
    width: 36,
    height: 36,
    "&:hover": {
      backgroundColor: tokens.color.brand.primaryActionHover,
    },
    "&:disabled": {
      backgroundColor: tokens.color.border.default,
      color: tokens.color.text.secondary,
    },
  },
}));

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

/**
 * Chat Input Component
 * Handles user message input and submission
 */
const ChatInput = ({ onSend, disabled }: ChatInputProps) => {
  const classes = useStyles();
  const [input, setInput] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (input.trim() && !disabled) {
      onSend(input.trim());
      setInput("");
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      className={classes.inputContainer}
    >
      <TextField
        className={classes.textField}
        variant="outlined"
        placeholder="Ask about the case..."
        multiline
        maxRows={4}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        size="small"
      />
      <IconButton
        type="submit"
        className={classes.sendButton}
        disabled={!input.trim() || disabled}
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
};

export default ChatInput;
